/**
 * The emoji the chat screen offers, and the small rules around them.
 *
 * Gordon, 18 October: "add emojis to the chat in a similar fashion to slack,
 * whatsapp and teams". Three things come out of that: a picker beside the
 * paperclip, Slack's ":thumbsup:" shortcodes typed straight into the box, and
 * reactions under a message. All three read from this file.
 *
 * A CURATED SET, NOT ALL OF UNICODE. About six hundred of the ones people
 * actually send at work, grouped the way every picker groups them, each with
 * a Slack-style shortcode, its Unicode name and a few words somebody might
 * search for ("bug" finds the beetle, "deploy" the rocket). The full list is
 * nearly four thousand with every skin tone, most of which this firm will
 * never send, and the npm packages that carry it would add more to the
 * download than the whole chat screen weighs.
 *
 * IMPORTS NOTHING, on purpose - e2e/emoji.mjs loads it straight into Node
 * the way e2e/session-roles.mjs loads its rule file, with no browser and no
 * bundler. Anything that touches the browser (remembering recent picks) takes
 * its storage as an argument or reaches for `window` only inside a try.
 *
 * One line per emoji: character | shortcode | name | search words.
 */

const SMILEYS = `
😀|grinning|grinning face|smile happy
😃|smiley|grinning face with big eyes|smile happy joy
😄|smile|grinning face with smiling eyes|happy joy laugh
😁|grin|beaming face with smiling eyes|happy smile teeth
😆|laughing|grinning squinting face|laugh happy satisfied
😅|sweat_smile|grinning face with sweat|relief phew nervous
🤣|rofl|rolling on the floor laughing|laugh lol
😂|joy|face with tears of joy|laugh lol cry funny
🙂|slightly_smiling_face|slightly smiling face|smile ok
🙃|upside_down_face|upside-down face|silly sarcasm
😉|wink|winking face|flirt joke
😊|blush|smiling face with smiling eyes|happy proud shy
😇|innocent|smiling face with halo|angel good
🥰|smiling_face_with_hearts|smiling face with hearts|love crush adore
😍|heart_eyes|smiling face with heart-eyes|love crush
🤩|star_struck|star-struck|wow amazing excited
😘|kissing_heart|face blowing a kiss|kiss love
😗|kissing|kissing face|kiss
😚|kissing_closed_eyes|kissing face with closed eyes|kiss
😋|yum|face savoring food|tasty delicious
😛|stuck_out_tongue|face with tongue|silly playful
😜|stuck_out_tongue_winking_eye|winking face with tongue|silly joke crazy
🤪|zany_face|zany face|crazy goofy
😝|stuck_out_tongue_closed_eyes|squinting face with tongue|silly
🤑|money_mouth_face|money-mouth face|rich cash
🤗|hugging_face|hugging face|hug thanks
🤭|hand_over_mouth|face with hand over mouth|oops giggle
🤫|shushing_face|shushing face|quiet secret
🤔|thinking_face|thinking face|think hmm consider
🤐|zipper_mouth_face|zipper-mouth face|secret quiet
🤨|raised_eyebrow|face with raised eyebrow|skeptical doubt
😐|neutral_face|neutral face|meh blank
😑|expressionless|expressionless face|blank meh
😶|no_mouth|face without mouth|speechless silent
😏|smirk|smirking face|smug sly
😒|unamused|unamused face|meh bored annoyed
🙄|roll_eyes|face with rolling eyes|eyeroll whatever
😬|grimacing|grimacing face|awkward eek
🤥|lying_face|lying face|lie pinocchio
😌|relieved|relieved face|calm relaxed
😔|pensive|pensive face|sad thoughtful
😪|sleepy|sleepy face|tired
🤤|drooling_face|drooling face|hungry
😴|sleeping|sleeping face|sleep zzz tired
😷|mask|face with medical mask|sick ill
🤒|face_with_thermometer|face with thermometer|sick ill fever
🤕|face_with_head_bandage|face with head-bandage|hurt injured
🤢|nauseated_face|nauseated face|sick gross
🤮|vomiting_face|face vomiting|sick puke
🤧|sneezing_face|sneezing face|sick cold
🥵|hot_face|hot face|heat sweating
🥶|cold_face|cold face|freezing frozen
🥴|woozy_face|woozy face|dizzy drunk
😵|dizzy_face|dizzy face|confused knocked out
🤯|exploding_head|exploding head|mind blown shocked
🤠|cowboy_hat_face|cowboy hat face|yeehaw
🥳|partying_face|partying face|party celebrate birthday
😎|sunglasses|smiling face with sunglasses|cool
🤓|nerd_face|nerd face|geek glasses
🧐|monocle_face|face with monocle|inspect curious
😕|confused|confused face|puzzled unsure
😟|worried|worried face|concerned nervous
🙁|slightly_frowning_face|slightly frowning face|sad
☹️|frowning_face|frowning face|sad
😮|open_mouth|face with open mouth|wow surprised
😯|hushed|hushed face|surprised
😲|astonished|astonished face|shocked amazed
😳|flushed|flushed face|embarrassed blush
🥺|pleading_face|pleading face|please puppy eyes
😦|frowning|frowning face with open mouth|shocked
😧|anguished|anguished face|shocked
😨|fearful|fearful face|scared afraid
😰|cold_sweat|anxious face with sweat|nervous stressed
😥|disappointed_relieved|sad but relieved face|phew
😢|cry|crying face|sad tear
😭|sob|loudly crying face|sad cry tears
😱|scream|face screaming in fear|scared horror
😖|confounded|confounded face|frustrated
😣|persevere|persevering face|struggle
😞|disappointed|disappointed face|sad
😓|sweat|downcast face with sweat|tired hard work
😩|weary|weary face|tired fed up
😫|tired_face|tired face|exhausted
🥱|yawning_face|yawning face|bored tired
😤|triumph|face with steam from nose|frustrated proud
😡|rage|pouting face|angry mad
😠|angry|angry face|mad annoyed
🤬|cursing_face|face with symbols on mouth|swear angry
😈|smiling_imp|smiling face with horns|devil
💀|skull|skull|dead dying laugh
💩|poop|pile of poo|poo
🤡|clown_face|clown face|clown
👻|ghost|ghost|halloween boo
👽|alien|alien|ufo
🤖|robot|robot|bot machine automation
😺|smiley_cat|grinning cat|cat happy
❤️|heart|red heart|love like
🧡|orange_heart|orange heart|love
💛|yellow_heart|yellow heart|love
💚|green_heart|green heart|love
💙|blue_heart|blue heart|love
💜|purple_heart|purple heart|love
🖤|black_heart|black heart|love
🤍|white_heart|white heart|love
💔|broken_heart|broken heart|sad heartbreak
❣️|heavy_heart_exclamation|heart exclamation|love
💕|two_hearts|two hearts|love
💖|sparkling_heart|sparkling heart|love
💯|100|hundred points|perfect score full
💢|anger|anger symbol|angry
💥|boom|collision|explosion bang
💫|dizzy|dizzy|star
💦|sweat_drops|sweat droplets|water
💬|speech_balloon|speech balloon|comment chat message
💭|thought_balloon|thought balloon|thinking
💤|zzz|zzz|sleep tired
`;

const PEOPLE = `
👋|wave|waving hand|hello hi bye
🤚|raised_back_of_hand|raised back of hand|stop
✋|raised_hand|raised hand|stop high five
🖖|vulcan_salute|vulcan salute|spock
👌|ok_hand|ok hand|okay perfect fine
🤌|pinched_fingers|pinched fingers|italian what
🤏|pinching_hand|pinching hand|small tiny
✌️|v|victory hand|peace
🤞|crossed_fingers|crossed fingers|luck hope
🤟|love_you_gesture|love-you gesture|ily
🤘|metal|sign of the horns|rock
🤙|call_me_hand|call me hand|phone shaka
👈|point_left|backhand index pointing left|left
👉|point_right|backhand index pointing right|right
👆|point_up_2|backhand index pointing up|up
👇|point_down|backhand index pointing down|down
☝️|point_up|index pointing up|one first
👍|thumbsup|thumbs up|+1 like yes agree approve good
👎|thumbsdown|thumbs down|-1 dislike no bad
✊|fist|raised fist|power solidarity
👊|punch|oncoming fist|fist bump
👏|clap|clapping hands|applause well done bravo
🙌|raised_hands|raising hands|hooray celebrate
👐|open_hands|open hands|hug
🤲|palms_up_together|palms up together|pray
🤝|handshake|handshake|deal agree meeting
🙏|pray|folded hands|please thanks thank you hope
✍️|writing_hand|writing hand|write sign
💅|nail_care|nail polish|whatever
🤳|selfie|selfie|camera phone
💪|muscle|flexed biceps|strong strength
👀|eyes|eyes|look see watching
👁️|eye|eye|look
👂|ear|ear|hear listen
👃|nose|nose|smell
🧠|brain|brain|smart think
👅|tongue|tongue|taste
👄|lips|mouth|kiss
👶|baby|baby|child
🧒|child|child|kid
👦|boy|boy|kid
👧|girl|girl|kid
🧑|adult|person|human
👨|man|man|male
👩|woman|woman|female
🧓|older_adult|older person|elderly
👴|older_man|old man|elderly grandpa
👵|older_woman|old woman|elderly grandma
👮|police_officer|police officer|cop
🕵️|detective|detective|spy investigate
💂|guard|guard|soldier
👷|construction_worker|construction worker|builder hard hat
🧑‍💻|technologist|technologist|developer coder laptop it computer
🧑‍🔧|mechanic|mechanic|repair fix technician tools
🧑‍🏫|teacher|teacher|school training
🧑‍⚕️|health_worker|health worker|doctor nurse
🧑‍🍳|cook|cook|chef
🧑‍💼|office_worker|office worker|business manager
🦸|superhero|superhero|hero
🧙|mage|mage|wizard magic
🙋|raising_hand|person raising hand|question me volunteer
🙅|no_good|person gesturing no|no stop
🙆|ok_woman|person gesturing ok|ok yes
💁|information_desk_person|person tipping hand|info sassy
🤷|shrug|person shrugging|dunno whatever idk
🤦|facepalm|person facepalming|doh ugh
🙇|bow|person bowing|sorry respect
🚶|walking|person walking|walk
🏃|runner|person running|run hurry rush
💃|dancer|woman dancing|dance party
🕺|man_dancing|man dancing|dance party
👯|dancers|people with bunny ears|party
🧘|lotus_position|person in lotus position|yoga calm meditate
🛌|sleeping_bed|person in bed|sleep
👪|family|family|parents kids
👫|couple|woman and man holding hands|couple
💑|couple_with_heart|couple with heart|love
🗣️|speaking_head|speaking head|talk speak
👤|bust_in_silhouette|bust in silhouette|user person
👥|busts_in_silhouette|busts in silhouette|users people team group
👣|footprints|footprints|steps
`;

const NATURE = `
🐶|dog|dog face|puppy pet
🐱|cat|cat face|kitten pet
🐭|mouse|mouse face|animal
🐹|hamster|hamster|pet
🐰|rabbit|rabbit face|bunny
🦊|fox_face|fox|animal
🐻|bear|bear|animal
🐼|panda_face|panda|animal
🐨|koala|koala|animal
🐯|tiger|tiger face|animal
🦁|lion|lion|animal
🐮|cow|cow face|animal
🐷|pig|pig face|animal
🐸|frog|frog|animal
🐵|monkey_face|monkey face|animal
🙈|see_no_evil|see-no-evil monkey|oops embarrassed
🙉|hear_no_evil|hear-no-evil monkey|monkey
🙊|speak_no_evil|speak-no-evil monkey|oops secret
🐔|chicken|chicken|bird
🐧|penguin|penguin|bird
🐦|bird|bird|animal
🦆|duck|duck|bird
🦅|eagle|eagle|bird
🦉|owl|owl|wise night
🐺|wolf|wolf|animal
🐴|horse|horse face|animal
🦄|unicorn|unicorn|magic
🐝|bee|honeybee|busy
🐛|bug|bug|caterpillar error defect
🦋|butterfly|butterfly|insect
🐌|snail|snail|slow
🐞|ladybug|lady beetle|bug insect
🐢|turtle|turtle|slow
🐍|snake|snake|python
🐙|octopus|octopus|sea
🦀|crab|crab|sea
🐠|tropical_fish|tropical fish|sea
🐟|fish|fish|sea
🐬|dolphin|dolphin|sea
🐳|whale|spouting whale|docker sea
🦈|shark|shark|sea
🐊|crocodile|crocodile|animal
🐘|elephant|elephant|animal
🐪|camel|camel|animal
🦒|giraffe|giraffe|animal
🐕|dog2|dog|pet
🐈|cat2|cat|pet
🐿️|chipmunk|chipmunk|squirrel
🦔|hedgehog|hedgehog|animal
💐|bouquet|bouquet|flowers
🌸|cherry_blossom|cherry blossom|flower spring
🌹|rose|rose|flower love
🌻|sunflower|sunflower|flower
🌷|tulip|tulip|flower
🌱|seedling|seedling|plant grow new
🌲|evergreen_tree|evergreen tree|tree
🌳|deciduous_tree|deciduous tree|tree
🌴|palm_tree|palm tree|holiday beach
🌵|cactus|cactus|desert
🍀|four_leaf_clover|four leaf clover|luck
🍁|maple_leaf|maple leaf|autumn
🍂|fallen_leaf|fallen leaf|autumn
🌍|earth_africa|globe showing europe-africa|world earth
🌙|crescent_moon|crescent moon|night
⭐|star|star|favourite
🌟|star2|glowing star|shine
✨|sparkles|sparkles|new shiny magic
⚡|zap|high voltage|lightning power fast electric
🔥|fire|fire|hot lit flame
🌈|rainbow|rainbow|pride
☀️|sunny|sun|weather hot
⛅|partly_sunny|sun behind cloud|weather
☁️|cloud|cloud|weather
🌧️|rain_cloud|cloud with rain|weather rain
⛈️|thunder_cloud_and_rain|cloud with lightning and rain|storm
❄️|snowflake|snowflake|cold winter
☃️|snowman|snowman|winter
🌊|ocean|water wave|sea
💧|droplet|droplet|water
`;

const FOOD = `
🍏|green_apple|green apple|fruit
🍎|apple|red apple|fruit
🍐|pear|pear|fruit
🍊|tangerine|tangerine|orange fruit
🍋|lemon|lemon|fruit
🍌|banana|banana|fruit
🍉|watermelon|watermelon|summer fruit
🍇|grapes|grapes|fruit
🍓|strawberry|strawberry|fruit
🍒|cherries|cherries|fruit
🍑|peach|peach|fruit
🥭|mango|mango|fruit
🍍|pineapple|pineapple|fruit
🥥|coconut|coconut|fruit
🥝|kiwi_fruit|kiwi fruit|fruit
🍅|tomato|tomato|vegetable
🥑|avocado|avocado|vegetable
🥦|broccoli|broccoli|vegetable
🌶️|hot_pepper|hot pepper|chilli spicy
🌽|corn|ear of corn|vegetable
🥕|carrot|carrot|vegetable
🥔|potato|potato|vegetable
🥐|croissant|croissant|breakfast
🍞|bread|bread|toast
🥖|baguette_bread|baguette bread|bread
🧀|cheese|cheese wedge|food
🥚|egg|egg|breakfast
🍳|fried_egg|cooking|breakfast
🥓|bacon|bacon|breakfast
🥞|pancakes|pancakes|breakfast
🍔|hamburger|hamburger|burger lunch
🍟|fries|french fries|chips
🍕|pizza|pizza|lunch
🌭|hotdog|hot dog|food
🥪|sandwich|sandwich|lunch
🌮|taco|taco|food
🌯|burrito|burrito|food
🥗|green_salad|green salad|healthy
🍝|spaghetti|spaghetti|pasta
🍜|ramen|steaming bowl|noodles
🍣|sushi|sushi|food
🍱|bento|bento box|lunch
🍿|popcorn|popcorn|movie
🍩|doughnut|doughnut|donut
🍪|cookie|cookie|biscuit
🎂|birthday|birthday cake|cake party
🍰|cake|shortcake|dessert
🧁|cupcake|cupcake|dessert
🍫|chocolate_bar|chocolate bar|sweet
🍬|candy|candy|sweet
🍦|icecream|soft ice cream|dessert
☕|coffee|hot beverage|coffee tea break
🍵|tea|teacup without handle|green tea
🥤|cup_with_straw|cup with straw|soda
🧃|beverage_box|beverage box|juice
🍺|beer|beer mug|drink pub
🍻|beers|clinking beer mugs|cheers drinks
🥂|clinking_glasses|clinking glasses|cheers celebrate champagne
🍷|wine_glass|wine glass|wine drink
🍸|cocktail|cocktail glass|drink
🍾|champagne|bottle with popping cork|celebrate party
🍴|fork_and_knife|fork and knife|lunch dinner
🥄|spoon|spoon|cutlery
`;

const ACTIVITIES = `
⚽|soccer|soccer ball|football sport
🏀|basketball|basketball|sport
🏈|football|american football|sport
⚾|baseball|baseball|sport
🎾|tennis|tennis|sport
🏐|volleyball|volleyball|sport
🏉|rugby_football|rugby football|sport
🎱|8ball|pool 8 ball|billiards
🏓|table_tennis_paddle_and_ball|ping pong|table tennis sport
🏸|badminton_racquet_and_shuttlecock|badminton|sport
⛳|golf|flag in hole|sport
🏹|bow_and_arrow|bow and arrow|archery
🎣|fishing_pole_and_fish|fishing pole|fishing
🥊|boxing_glove|boxing glove|fight
🥋|martial_arts_uniform|martial arts uniform|karate
⛸️|ice_skate|ice skate|skating
🎿|ski|skis|skiing
🏂|snowboarder|snowboarder|snow
🏋️|weight_lifter|person lifting weights|gym
🚴|bicyclist|person biking|cycle bike
🏊|swimmer|person swimming|swim
🏄|surfer|person surfing|surf
🏆|trophy|trophy|win winner award champion
🥇|first_place_medal|1st place medal|gold winner
🥈|second_place_medal|2nd place medal|silver
🥉|third_place_medal|3rd place medal|bronze
🏅|sports_medal|sports medal|award
🎖️|medal|military medal|award
🎗️|reminder_ribbon|reminder ribbon|ribbon
🎫|ticket|ticket|admission
🎟️|admission_tickets|admission tickets|event
🎪|circus_tent|circus tent|circus
🎭|performing_arts|performing arts|theatre
🎨|art|artist palette|paint design
🎬|clapper|clapper board|film movie
🎤|microphone|microphone|sing karaoke
🎧|headphones|headphone|music listen
🎼|musical_score|musical score|music
🎹|musical_keyboard|musical keyboard|piano
🥁|drum_with_drumsticks|drum|music
🎷|saxophone|saxophone|music
🎸|guitar|guitar|rock music
🎻|violin|violin|music
🎲|game_die|game die|dice
♟️|chess_pawn|chess pawn|strategy
🎯|dart|direct hit|target bullseye goal
🎳|bowling|bowling|sport
🎮|video_game|video game|controller gaming
🧩|jigsaw|puzzle piece|puzzle
🎉|tada|party popper|celebrate congrats hooray
🎊|confetti_ball|confetti ball|party celebrate
🎈|balloon|balloon|party birthday
🎁|gift|wrapped gift|present birthday
🎄|christmas_tree|christmas tree|xmas
🎃|jack_o_lantern|jack-o-lantern|halloween
🎆|fireworks|fireworks|celebrate new year
`;

const TRAVEL = `
🚗|car|automobile|car drive
🚕|taxi|taxi|cab
🚙|blue_car|sport utility vehicle|suv
🚌|bus|bus|transport
🚎|trolleybus|trolleybus|transport
🏎️|racing_car|racing car|fast
🚓|police_car|police car|police
🚑|ambulance|ambulance|emergency
🚒|fire_engine|fire engine|emergency
🚐|minibus|minibus|van
🚚|truck|delivery truck|delivery shipping
🚛|articulated_lorry|articulated lorry|truck
🚜|tractor|tractor|farm
🛵|motor_scooter|motor scooter|scooter
🚲|bike|bicycle|cycle
🛴|scooter|kick scooter|scooter
🚨|rotating_light|police car light|alert emergency urgent siren
🚥|traffic_light|horizontal traffic light|traffic
🚧|construction|construction|roadworks wip work in progress
⛽|fuelpump|fuel pump|petrol gas
🚏|busstop|bus stop|bus
🚦|vertical_traffic_light|vertical traffic light|traffic
🚢|ship|ship|boat
⛴️|ferry|ferry|boat gozo
⛵|boat|sailboat|sail
🚤|speedboat|speedboat|boat
⚓|anchor|anchor|ship
✈️|airplane|airplane|flight travel plane
🛫|airplane_departure|airplane departure|takeoff
🛬|airplane_arriving|airplane arrival|landing
🚁|helicopter|helicopter|flight
🚀|rocket|rocket|launch ship deploy fast
🛸|flying_saucer|flying saucer|ufo
🚆|train|train|rail
🚇|metro|metro|underground subway
🏠|house|house|home
🏡|house_with_garden|house with garden|home
🏢|office|office building|work
🏥|hospital|hospital|health
🏦|bank|bank|money
🏨|hotel|hotel|stay
🏪|convenience_store|convenience store|shop
🏫|school|school|education
🏭|factory|factory|industry
🏰|european_castle|castle|building
⛪|church|church|building
🗼|tokyo_tower|tokyo tower|landmark
🗽|statue_of_liberty|statue of liberty|landmark
⛲|fountain|fountain|park
🏖️|beach_with_umbrella|beach with umbrella|holiday summer
🏝️|desert_island|desert island|holiday
⛰️|mountain|mountain|hill
🌋|volcano|volcano|mountain
🗺️|world_map|world map|map
🧭|compass|compass|direction
🌃|night_with_stars|night with stars|city
🌅|sunrise|sunrise|morning
🌇|city_sunset|sunset|evening
🏙️|cityscape|cityscape|city
🌉|bridge_at_night|bridge at night|city
🎡|ferris_wheel|ferris wheel|fair
🎢|roller_coaster|roller coaster|fair
⌛|hourglass|hourglass done|time wait
⏳|hourglass_flowing_sand|hourglass not done|waiting loading time
⌚|watch|watch|time
⏰|alarm_clock|alarm clock|time wake reminder
⏱️|stopwatch|stopwatch|timer
🕐|clock1|one o'clock|time
`;

const OBJECTS = `
💻|computer|laptop|pc work it
🖥️|desktop_computer|desktop computer|pc monitor screen
🖨️|printer|printer|print
⌨️|keyboard|keyboard|type
🖱️|computer_mouse|computer mouse|click
💾|floppy_disk|floppy disk|save
💿|cd|optical disk|disc
📀|dvd|dvd|disc
📱|iphone|mobile phone|phone smartphone cell
📲|calling|mobile phone with arrow|call
☎️|phone|telephone|call ring
📞|telephone_receiver|telephone receiver|call phone
📟|pager|pager|beeper
📠|fax|fax machine|fax
🔋|battery|battery|power charge
🔌|electric_plug|electric plug|power cable
💡|bulb|light bulb|idea tip
🔦|flashlight|flashlight|torch
🕯️|candle|candle|light
📷|camera|camera|photo
📸|camera_with_flash|camera with flash|photo screenshot
📹|video_camera|video camera|record
🎥|movie_camera|movie camera|film
📺|tv|television|screen
📻|radio|radio|music
🎙️|studio_microphone|studio microphone|podcast
🔍|mag|magnifying glass tilted left|search find zoom
🔎|mag_right|magnifying glass tilted right|search find
🔒|lock|locked|security private password
🔓|unlock|unlocked|open security
🔐|closed_lock_with_key|locked with key|secure
🔑|key|key|password access
🗝️|old_key|old key|key
🔨|hammer|hammer|tool fix build
🔧|wrench|wrench|tool fix settings repair
🔩|nut_and_bolt|nut and bolt|hardware
⚙️|gear|gear|settings config cog
🛠️|hammer_and_wrench|hammer and wrench|tools fix repair build
🧰|toolbox|toolbox|tools repair
🧲|magnet|magnet|attract
🧪|test_tube|test tube|test experiment lab
💊|pill|pill|medicine
🩹|adhesive_bandage|adhesive bandage|plaster fix
🛒|shopping_trolley|shopping cart|buy order
🧾|receipt|receipt|invoice bill
💳|credit_card|credit card|payment pay
💰|moneybag|money bag|cash budget
💵|dollar|dollar banknote|money cash
💶|euro|euro banknote|money cash
✉️|email|envelope|mail letter
📧|e-mail|e-mail|email mail
📨|incoming_envelope|incoming envelope|mail
📩|envelope_with_arrow|envelope with arrow|mail send
📤|outbox_tray|outbox tray|send upload
📥|inbox_tray|inbox tray|receive download
📦|package|package|box parcel delivery shipping
📫|mailbox|closed mailbox with raised flag|mail post
📝|memo|memo|note write pencil document
📄|page_facing_up|page facing up|document file
📃|page_with_curl|page with curl|document
📑|bookmark_tabs|bookmark tabs|document
📊|bar_chart|bar chart|stats report graph
📈|chart_with_upwards_trend|chart increasing|growth up stats
📉|chart_with_downwards_trend|chart decreasing|down stats
📋|clipboard|clipboard|list checklist
📌|pushpin|pushpin|pin important
📍|round_pushpin|round pushpin|location place
📎|paperclip|paperclip|attach attachment
✂️|scissors|scissors|cut
🖊️|lower_left_ballpoint_pen|pen|write sign
✏️|pencil2|pencil|write edit
📁|file_folder|file folder|folder directory
📂|open_file_folder|open file folder|folder
🗂️|card_index_dividers|card index dividers|organise
📅|date|calendar|date schedule
📆|calendar|tear-off calendar|date schedule
🗓️|spiral_calendar_pad|spiral calendar|schedule
🗑️|wastebasket|wastebasket|delete bin trash
📚|books|books|library read
📖|book|open book|read manual docs
🔗|link|link|url chain
🏷️|label|label|tag
🔔|bell|bell|notification alert ring
🔕|no_bell|bell with slash|mute silent
📢|loudspeaker|loudspeaker|announcement
📣|mega|megaphone|announcement
💼|briefcase|briefcase|work business
🎒|school_satchel|backpack|bag
👓|eyeglasses|glasses|see
☂️|umbrella|umbrella|rain
🧹|broom|broom|clean sweep
🚪|door|door|exit
🪑|chair|chair|seat desk
🛏️|bed|bed|sleep
`;

const SYMBOLS = `
✅|white_check_mark|check mark button|done yes ok complete tick
☑️|ballot_box_with_check|check box with check|done
✔️|heavy_check_mark|check mark|done tick yes
❌|x|cross mark|no wrong cancel error
❎|negative_squared_cross_mark|cross mark button|no
➕|heavy_plus_sign|plus|add
➖|heavy_minus_sign|minus|remove
❓|question|red question mark|question help what
❔|grey_question|white question mark|question
❗|exclamation|red exclamation mark|important warning
‼️|bangbang|double exclamation mark|important
⚠️|warning|warning|alert caution danger
⛔|no_entry|no entry|stop forbidden
🚫|no_entry_sign|prohibited|forbidden blocked
🛑|octagonal_sign|stop sign|stop
♻️|recycle|recycling symbol|recycle
🔄|arrows_counterclockwise|counterclockwise arrows button|refresh sync reload retry
🔁|repeat|repeat button|loop again
🔃|arrows_clockwise|clockwise vertical arrows|reload
🔀|twisted_rightwards_arrows|shuffle tracks button|shuffle
➡️|arrow_right|right arrow|next
⬅️|arrow_left|left arrow|back previous
⬆️|arrow_up|up arrow|up
⬇️|arrow_down|down arrow|down
↩️|leftwards_arrow_with_hook|right arrow curving left|reply undo
🔝|top|top arrow|top
🆕|new|new button|new
🆗|ok|ok button|okay
🆘|sos|sos button|help emergency
🆙|up|up! button|up
🔴|red_circle|red circle|urgent stop
🟠|large_orange_circle|orange circle|circle
🟡|large_yellow_circle|yellow circle|circle
🟢|large_green_circle|green circle|online go ok
🔵|large_blue_circle|blue circle|circle
⚪|white_circle|white circle|circle
⚫|black_circle|black circle|circle
🟥|large_red_square|red square|square
🟩|large_green_square|green square|square
🔶|large_orange_diamond|large orange diamond|diamond
🔷|large_blue_diamond|large blue diamond|diamond
ℹ️|information_source|information|info
🔢|1234|input numbers|numbers
#️⃣|hash|keycap number sign|hashtag
1️⃣|one|keycap 1|one number
2️⃣|two|keycap 2|two number
3️⃣|three|keycap 3|three number
🔟|keycap_ten|keycap 10|ten
©️|copyright|copyright|legal
®️|registered|registered|legal
™️|tm|trade mark|trademark
💲|heavy_dollar_sign|heavy dollar sign|money
💱|currency_exchange|currency exchange|money
🔅|low_brightness|dim button|brightness
🔆|high_brightness|bright button|brightness
📶|signal_strength|antenna bars|signal wifi network
📴|mobile_phone_off|mobile phone off|off
🔇|mute|muted speaker|silent
🔊|loud_sound|speaker high volume|sound
🎵|musical_note|musical note|music
🎶|notes|musical notes|music
♾️|infinity|infinity|forever
▶️|arrow_forward|play button|play
⏸️|double_vertical_bar|pause button|pause
⏹️|black_square_for_stop|stop button|stop
⏺️|black_circle_for_record|record button|record
⏩|fast_forward|fast-forward button|skip
☮️|peace_symbol|peace symbol|peace
☯️|yin_yang|yin yang|balance
🏁|checkered_flag|chequered flag|finish done race
🚩|triangular_flag_on_post|triangular flag|flag issue
🏳️|waving_white_flag|white flag|surrender
`;

const FLAGS = `
🇲🇹|flag-mt|flag: Malta|malta mt
🇪🇺|flag-eu|flag: European Union|europe eu
🇬🇧|flag-gb|flag: United Kingdom|uk britain england gb
🇮🇹|flag-it|flag: Italy|italy it
🇫🇷|flag-fr|flag: France|france fr
🇩🇪|flag-de|flag: Germany|germany de
🇪🇸|flag-es|flag: Spain|spain es
🇵🇹|flag-pt|flag: Portugal|portugal pt
🇳🇱|flag-nl|flag: Netherlands|netherlands holland nl
🇮🇪|flag-ie|flag: Ireland|ireland ie
🇬🇷|flag-gr|flag: Greece|greece gr
🇨🇭|flag-ch|flag: Switzerland|switzerland ch
🇺🇦|flag-ua|flag: Ukraine|ukraine ua
🇺🇸|flag-us|flag: United States|usa america us
🇨🇦|flag-ca|flag: Canada|canada ca
🇦🇺|flag-au|flag: Australia|australia au
🇮🇳|flag-in|flag: India|india in
🇯🇵|flag-jp|flag: Japan|japan jp
🏳️‍🌈|rainbow-flag|rainbow flag|pride lgbt
`;

/** The tabs along the top of the picker, in the order every picker uses. */
export const CATEGORIES = [
  { id: 'recent', label: 'Recently used', icon: '🕘' },
  { id: 'smileys', label: 'Smileys & emotion', icon: '😀', list: SMILEYS },
  { id: 'people', label: 'People & gestures', icon: '👋', list: PEOPLE },
  { id: 'nature', label: 'Animals & nature', icon: '🐶', list: NATURE },
  { id: 'food', label: 'Food & drink', icon: '☕', list: FOOD },
  { id: 'activities', label: 'Activities', icon: '⚽', list: ACTIVITIES },
  { id: 'travel', label: 'Travel & places', icon: '🚗', list: TRAVEL },
  { id: 'objects', label: 'Objects', icon: '💡', list: OBJECTS },
  { id: 'symbols', label: 'Symbols', icon: '✅', list: SYMBOLS },
  { id: 'flags', label: 'Flags', icon: '🏁', list: FLAGS }
].map(({ list, ...c }) => ({
  ...c,
  emoji: (list || '').split('\n').map((l) => l.trim()).filter(Boolean).map((line) => {
    const [char, code, name, words] = line.split('|');
    return {
      char, code, name, category: c.id,
      words: `${code.replace(/_/g, ' ')} ${name} ${words || ''}`.toLowerCase()
        .split(/[\s:,-]+/).filter(Boolean)
    };
  })
}));

/** Every emoji, once, in picker order. */
export const EMOJI = CATEGORIES.flatMap((c) => c.emoji);

const BY_CODE = new Map(EMOJI.map((e) => [e.code, e]));
const BY_CHAR = new Map(EMOJI.map((e) => [e.char, e]));

/*
 * The other names Slack answers to. ":+1:" is how half the world types a
 * thumbs up, and nobody should have to learn that this picker calls it
 * "thumbsup" instead.
 */
const ALIASES = {
  '+1': 'thumbsup', '-1': 'thumbsdown', thumbs_up: 'thumbsup', thumbs_down: 'thumbsdown',
  like: 'thumbsup', laugh: 'joy', lol: 'joy', satisfied: 'laughing', hankey: 'poop',
  shit: 'poop', red_heart: 'heart', thanks: 'pray', thank_you: 'pray', party: 'tada',
  check: 'white_check_mark', tick: 'white_check_mark', ok_hand_sign: 'ok_hand',
  slightly_smiling: 'slightly_smiling_face', thinking: 'thinking_face', facepalm_person: 'facepalm',
  mt: 'flag-mt', eu: 'flag-eu', gb: 'flag-gb', uk: 'flag-gb', it: 'flag-it'
};

/** The emoji a shortcode names, without its colons, or null. */
export const emojiFor = (code) => {
  const c = String(code || '').toLowerCase();
  return BY_CODE.get(c) || BY_CODE.get(ALIASES[c]) || null;
};

/** What we know about a character, for the picker's preview line. */
export const aboutEmoji = (char) => BY_CHAR.get(char) || null;

/** The six under every message, in the order WhatsApp and Teams settled on. */
export const QUICK_REACTIONS = ['👍', '❤️', '😂', '😮', '😢', '🙏'];

/*
 * SEARCH. Every word typed must start one of the emoji's words - its
 * shortcode, its name or a keyword - so "thumbs" finds both thumbs and
 * "thu up" finds only one. Ranked so that the shortcode beginning with what
 * was typed comes first: someone typing "fire" wants the flame, not the
 * fire engine.
 */
export const searchEmoji = (query, limit = 60) => {
  const terms = String(query || '').toLowerCase().replace(/^:+|:+$/g, '')
    .split(/[\s_:]+/).filter(Boolean);
  if (terms.length === 0) return [];
  const scored = [];
  EMOJI.forEach((e, i) => {
    let score = 0;
    for (const t of terms) {
      if (!e.words.some((w) => w.startsWith(t))) { score = -1; break; }
    }
    if (score < 0) return;
    const whole = terms.join('_');
    if (e.code === whole) score = 0;
    else if (e.code.startsWith(whole)) score = 1;
    else if (e.name.toLowerCase().startsWith(terms[0])) score = 2;
    else score = 3;
    scored.push({ e, score, i });
  });
  scored.sort((a, b) => a.score - b.score || a.i - b.i);
  return scored.slice(0, limit).map((s) => s.e);
};

/* ----------------------------------------------------------- shortcodes */

/*
 * WHEN ":" STARTS A SHORTCODE. Only at the start of the box or after a space
 * or an opening bracket, and only once two letters follow it - so the colon
 * in "10:30", in "https://", in "Note:" or in a smiley typed the old way
 * ":)" never pops a list up over what somebody is writing.
 */
const OPEN_CODE = /(^|[\s([{])(:([a-z][a-z0-9_+-]+))$/i;

/**
 * The shortcode being typed just before the caret, as {start, query}, or
 * null. `start` is where its colon is, which is what gets replaced.
 */
export const shortcodeAt = (text, caret) => {
  const before = String(text || '').slice(0, caret ?? String(text || '').length);
  const m = OPEN_CODE.exec(before);
  if (!m) return null;
  return { start: before.length - m[2].length, query: m[3] };
};

const CLOSED_CODE = /(^|[\s([{])(:([a-z0-9_+-]{2,}):)$/i;

/**
 * A finished ":thumbsup:" just before the caret, turned into its emoji. Gives
 * back the new text and where the caret goes, or null when what is there is
 * not a code we know - ":lunch:" stays as typed rather than vanishing.
 */
export const completeShortcode = (text, caret) => {
  const s = String(text || '');
  const at = caret ?? s.length;
  const before = s.slice(0, at);
  const m = CLOSED_CODE.exec(before);
  if (!m) return null;
  const hit = emojiFor(m[3]);
  if (!hit) return null;
  const start = before.length - m[2].length;
  return { text: s.slice(0, start) + hit.char + s.slice(at), caret: start + hit.char.length };
};

/* -------------------------------------------------------- big emoji */

/*
 * One emoji as the reader sees it: a flag (two regional letters), a keycap,
 * or a picture with its variation selector, skin tone and any zero-width
 * joined parts ("technologist" is a person, a joiner and a laptop).
 *
 * Built from a string, not written as a /literal/: the build would otherwise
 * spell out every Unicode property as its thousands of code-point ranges
 * for browsers that predate them, about ten kilobytes of download for one
 * regular expression. A browser that cannot build it simply never draws an
 * emoji large.
 */
const ONE_EMOJI = (() => {
  try {
    return new RegExp('(?:\\p{Regional_Indicator}{2}|[#*0-9]\\uFE0F?\\u20E3'
      + '|\\p{Extended_Pictographic}\\uFE0F?\\p{Emoji_Modifier}?'
      + '(?:\\u200D\\p{Extended_Pictographic}\\uFE0F?\\p{Emoji_Modifier}?)*)', 'gu');
  } catch {
    return null;
  }
})();

/**
 * How many emoji a message is, when it is nothing but emoji - 0 otherwise.
 * WhatsApp and Slack both draw a message of one to three emoji and nothing
 * else large, and this is the test for it.
 */
export const emojiOnlyCount = (text) => {
  const s = String(text || '').trim();
  if (!s || !ONE_EMOJI) return 0;
  const found = s.match(ONE_EMOJI) || [];
  const rest = s.replace(ONE_EMOJI, '').replace(/[\s️‍]/g, '');
  return rest === '' ? found.length : 0;
};

export const isJumbo = (text) => {
  const n = emojiOnlyCount(text);
  return n >= 1 && n <= 3;
};

/* ---------------------------------------------------- recently used */

/*
 * RECENTLY USED, kept in this browser: a preference about a screen, like the
 * folded sections of the list. Wrapped, because storage can be switched off
 * or full, and a picker that will not open over that would be a strange way
 * to fail. Most recent first, no repeats, a picker row or two of them.
 */
export const RECENT_KEY = 'jarvis.chat.emoji.recent';
export const RECENT_MAX = 24;

const storageOf = (storage) => {
  if (storage) return storage;
  try { return typeof window !== 'undefined' ? window.localStorage : null; } catch { return null; }
};

export const readRecent = (storage) => {
  try {
    const got = JSON.parse(storageOf(storage)?.getItem(RECENT_KEY) || '[]');
    return Array.isArray(got) ? got.filter((c) => typeof c === 'string' && c).slice(0, RECENT_MAX) : [];
  } catch {
    return [];
  }
};

export const rememberRecent = (char, storage) => {
  const next = [char, ...readRecent(storage).filter((c) => c !== char)].slice(0, RECENT_MAX);
  try { storageOf(storage)?.setItem(RECENT_KEY, JSON.stringify(next)); } catch { /* not kept */ }
  return next;
};

/* ------------------------------------------------- who reacted, in words */

/**
 * "Rita, Marco and you" - who put one reaction on a message, the way Slack
 * says it in the tooltip. `names` is other people as the server sends them;
 * `me` is how this browser's person is named, so a server that lists them
 * by name as well does not have them said twice.
 */
export const whoReacted = ({ names, mine } = {}, me) => {
  const mineName = String(me || '').trim().toLowerCase();
  const others = (Array.isArray(names) ? names : [])
    .map((n) => String(n || '').trim()).filter(Boolean)
    .filter((n) => n.toLowerCase() !== 'you' && (!mineName || n.toLowerCase() !== mineName));
  const all = mine ? [...others, 'you'] : others;
  if (all.length === 0) return '';
  if (all.length === 1) return all[0];
  return `${all.slice(0, -1).join(', ')} and ${all[all.length - 1]}`;
};

/**
 * One reaction toggled by me, before the server has answered: added with me
 * in it, or taken off - and gone altogether once nobody is left on it.
 */
export const toggleReaction = (reactions, emoji) => {
  const list = Array.isArray(reactions) ? reactions : [];
  const at = list.findIndex((r) => r.emoji === emoji);
  if (at < 0) return [...list, { emoji, count: 1, mine: true, names: [] }];
  const r = list[at];
  const next = r.mine
    ? { ...r, mine: false, count: Math.max(0, (Number(r.count) || 1) - 1) }
    : { ...r, mine: true, count: (Number(r.count) || 0) + 1 };
  return next.count > 0
    ? list.map((x, i) => (i === at ? next : x))
    : list.filter((_, i) => i !== at);
};
